import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { fetchMySessions, fetchActiveExam } from '../../../api/sessions';
import { fetchUserDashboard } from '../../../api/admin';
import { useTopics } from '../../../hooks/useQuestions';
import { useCertStore } from '../../../stores/certStore';
import type { SessionSummary } from '../../../types';
import { Card, Modal } from '../../ui';
import { CMIcon, DomainGlyph, MiniBar, Mono } from '../../ui/primitives';
import { BAR_FILL_CLASS, getIqTier } from '../../dashboard/variantB/palette';
import SessionLauncher from '../SessionLauncher';
import { buildDomainStats, type DomainStat } from './planData';
import { resolveTopicName } from './resolveTopic';
import { useLaunchSession } from './useLaunchSession';

/* ── Localized relative timestamp ───────────────────────────────────────── */
function timeAgo(dateStr: string, t: TFunction): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return t('study.mix.resume.justNow');
  if (mins < 60) return t('study.mix.resume.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('study.mix.resume.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('study.mix.resume.daysAgo', { count: days });
}

/* ── Section label with a trailing hairline ─────────────────────────────── */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 flex items-center gap-2.5 text-[10.5px] font-extrabold uppercase tracking-[0.11em] text-slate-400 dark:text-slate-500">
      <span>{children}</span>
      <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" aria-hidden="true" />
    </div>
  );
}

/* ── Difficulty chip (hard/med/easy → red/amber/green) ──────────────────── */
function DifficultyChip({ value }: { value: string }) {
  const v = value.trim().toLowerCase();
  const tone = v.startsWith('hard') || v.includes('diff')
    ? 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300'
    : v.startsWith('eas') || v.includes('facile')
    ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300'
    : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide ${tone}`}>{value}</span>;
}

/* ── Resume hero — the one obvious action ───────────────────────────────── */
function ResumeHero({ onSmart, smartPending }: { onSmart: () => void; smartPending: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const certCode = useCertStore((s) => s.certCode);

  const { data, isLoading } = useQuery({
    queryKey: ['my-sessions', certCode, 'study', 'active', 'hero'],
    queryFn: () => fetchMySessions({ mode: 'study', active_only: true, limit: 1 }, certCode),
    staleTime: 30_000,
  });

  const session: SessionSummary | undefined = data?.items?.[0];

  // Gradient border shell (blue-dominant, matches the mockup's glowing card).
  const shell = (inner: React.ReactNode) => (
    <div
      className="relative overflow-hidden rounded-[20px] p-0.5"
      style={{ background: 'linear-gradient(120deg, #2563EB, #4F46E5 60%, #7C3AED)' }}
    >
      <div className="rounded-[18px] bg-white p-[22px_24px] dark:bg-card-dark">{inner}</div>
    </div>
  );

  if (isLoading) {
    return shell(<div className="h-[92px] animate-pulse rounded-xl bg-slate-100 dark:bg-white/5" />);
  }

  // Empty state — no in-progress session: lead straight into a smart session.
  if (!session) {
    return shell(
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <span className="text-[10.5px] font-extrabold uppercase tracking-[0.09em] text-blue-600 dark:text-blue-400">
            {t('study.mix.hero.emptyKicker')}
          </span>
          <h2 className="mt-1.5 text-[20px] font-extrabold leading-tight tracking-[-0.02em] text-slate-900 dark:text-white">
            {t('study.mix.hero.emptyTitle')}
          </h2>
          <p className="mt-1 text-[13px] leading-snug text-slate-600 dark:text-slate-300">{t('study.mix.hero.emptySub')}</p>
        </div>
        <button
          type="button"
          onClick={onSmart}
          disabled={smartPending}
          className="inline-flex flex-none items-center gap-2 rounded-[12px] bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-[0_8px_20px_-8px_rgba(37,99,235,0.7)] transition-all hover:bg-blue-700 disabled:opacity-50"
        >
          <CMIcon name="sparkles" size={17} fill /> {t('study.mix.hero.emptyCta')}
        </button>
      </div>,
    );
  }

  const pct = session.total_questions > 0 ? Math.round((session.cursor_global / session.total_questions) * 100) : 0;
  const resume = () => navigate(`/study/session/${session.id}`);

  return shell(
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3.5">
          <span
            className="grid h-[44px] w-[44px] flex-none place-items-center rounded-[13px] text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)]"
            style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)' }}
          >
            <CMIcon name="layers" size={21} />
          </span>
          <div className="min-w-0">
            <span className="text-[10.5px] font-extrabold uppercase tracking-[0.09em] text-blue-600 dark:text-blue-400">
              {t('study.mix.hero.currentKicker')}
            </span>
            <h2 className="mt-0.5 truncate text-[21px] font-extrabold leading-tight tracking-[-0.02em] text-slate-900 dark:text-white">
              {session.session_name}
            </h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px] font-semibold text-slate-500 dark:text-slate-400">
              <span>{t('study.mix.resume.questions', { cursor: session.cursor_global, total: session.total_questions })}</span>
              {session.updated_at && (
                <>
                  <span className="h-[3px] w-[3px] rounded-full bg-slate-400 dark:bg-slate-500" aria-hidden="true" />
                  <span>{timeAgo(session.updated_at, t)}</span>
                </>
              )}
              {session.difficulty_filter && (
                <>
                  <span className="h-[3px] w-[3px] rounded-full bg-slate-400 dark:bg-slate-500" aria-hidden="true" />
                  <DifficultyChip value={session.difficulty_filter} />
                </>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={resume}
          className="inline-flex flex-none items-center gap-2 rounded-[12px] bg-blue-600 px-5 py-3 text-sm font-bold text-white shadow-[0_8px_20px_-8px_rgba(37,99,235,0.7)] transition-all hover:bg-blue-700"
        >
          <CMIcon name="play" size={16} fill /> {t('study.mix.resume.resume')}
        </button>
      </div>

      <div className="flex items-center gap-3">
        <MiniBar value={pct} fillClass={BAR_FILL_CLASS.blue} className="flex-1" />
        <Mono className="w-[38px] flex-none text-right text-[12px] font-extrabold text-slate-900 dark:text-white">{pct}%</Mono>
      </div>
    </div>,
  );
}

/* ── Secondary action card (smart session / mock exam) ──────────────────── */
function ActionCard({
  icon,
  gradient,
  title,
  desc,
  onClick,
  disabled,
}: {
  icon: string;
  gradient: string;
  title: string;
  desc: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <Card
      interactive
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => !disabled && onClick()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      className={`group flex cursor-pointer items-center gap-3.5 p-[16px_18px] ${disabled ? 'pointer-events-none opacity-60' : ''}`}
    >
      <span className="grid h-[42px] w-[42px] flex-none place-items-center rounded-[12px] text-white" style={{ background: gradient }}>
        <CMIcon name={icon} size={20} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-extrabold tracking-[-0.01em] text-slate-900 dark:text-white">{title}</div>
        <div className="mt-0.5 text-[11.5px] leading-snug text-slate-500 dark:text-slate-400">{desc}</div>
      </div>
      <CMIcon
        name="chevright"
        size={17}
        className="flex-none text-slate-300 transition-colors group-hover:text-blue-600 dark:text-slate-600 dark:group-hover:text-blue-400"
      />
    </Card>
  );
}

/* ── Domain tiles (live mastery) + custom-session tile ──────────────────── */
function DomainGrid({ onLaunchDomain, onCustom }: { onLaunchDomain: (key: string, name: string) => void; onCustom: () => void }) {
  const { t } = useTranslation();
  const certCode = useCertStore((s) => s.certCode);

  const { data: dashboard } = useQuery({
    queryKey: ['user-dashboard', certCode, 'study-mix'],
    queryFn: () => fetchUserDashboard(certCode),
    staleTime: 60_000,
  });

  const stats: DomainStat[] = useMemo(() => buildDomainStats(dashboard?.topic_mastery), [dashboard]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {stats.map((d) => {
        const started = d.mastery !== null;
        const tier = started ? getIqTier(d.mastery as number) : null;
        const name = t(`study.mix.domains.${d.key}`);
        return (
          <Card
            key={d.code}
            interactive
            role="button"
            tabIndex={0}
            onClick={() => onLaunchDomain(d.key, name)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onLaunchDomain(d.key, name);
              }
            }}
            className="group relative flex cursor-pointer flex-col gap-3 p-4"
          >
            <div className="flex items-center gap-2.5">
              <DomainGlyph icon={d.icon} gradient={d.gradient} size={34} radius={10} iconScale={0.5} />
              <span className="flex-1 text-[13px] font-extrabold leading-tight tracking-[-0.01em] text-slate-900 dark:text-white">{name}</span>
              {started ? (
                <Mono className="text-[15px] font-extrabold text-slate-900 dark:text-white">
                  {d.mastery}
                  <small className="ml-px text-[9.5px] font-extrabold text-slate-400 dark:text-slate-500">{t('study.mix.domains.pct')}</small>
                </Mono>
              ) : (
                <span className="text-[10px] font-extrabold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  {t('study.mix.domains.new')}
                </span>
              )}
            </div>
            <MiniBar value={started ? (d.mastery as number) : 0} fillClass={tier ? BAR_FILL_CLASS[tier.barColor] : 'bg-slate-300 dark:bg-white/15'} tickAt={70} />
            <span className="absolute bottom-3 right-3.5 inline-flex items-center gap-1 text-[11.5px] font-extrabold text-blue-600 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 dark:text-blue-400">
              {t('study.mix.domains.practice')} <CMIcon name="arrowright" size={12} />
            </span>
          </Card>
        );
      })}

      {/* Custom session tile (dashed) */}
      <button
        type="button"
        onClick={onCustom}
        className="flex min-h-[86px] items-center justify-center gap-2.5 rounded-2xl border border-dashed border-slate-300 bg-transparent text-[13px] font-bold text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-900 dark:border-white/15 dark:text-slate-400 dark:hover:border-white/30 dark:hover:text-white"
      >
        <CMIcon name="settings" size={17} /> {t('study.mix.domains.custom')}
      </button>
    </div>
  );
}

/**
 * Study Mode "Mix" — a one-action launcher. A single obvious CTA (resume the
 * in-progress session) heads the screen; a smart session and a mock exam sit
 * one click away; per-domain practice tiles (live mastery from the dashboard)
 * follow. The full topic/difficulty composer is demoted to the dashed
 * "Custom session…" tile, opened in a modal.
 */
export default function StudyMix() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const certCode = useCertStore((s) => s.certCode);
  const [customOpen, setCustomOpen] = useState(false);
  const { launch, launchSmart, defaultName, isPending } = useLaunchSession();

  const { data: topics } = useTopics();
  const topicNames = useMemo(() => (topics ?? []).map((tp) => tp.topic), [topics]);

  // Show the mock-exam card as "resume" when an exam is already in progress.
  const { data: activeExam } = useQuery({
    queryKey: ['active-exam', certCode, 'study-mix'],
    queryFn: () => fetchActiveExam(certCode),
    staleTime: 30_000,
  });

  const startSmart = () => launchSmart(defaultName(t('study.mix.smart.title')));
  const handleLaunchDomain = (key: string, name: string) =>
    launch({ name: defaultName(name), topicFilter: resolveTopicName(key, topicNames) });

  return (
    <div className="mx-auto flex w-full max-w-[900px] flex-col gap-[26px]">
      {/* Header */}
      <div>
        <span className="text-[11px] font-extrabold uppercase tracking-[0.13em] text-blue-600 dark:text-blue-400">{t('study.mix.kicker')}</span>
        <h1 className="mt-2 text-balance text-[30px] font-extrabold leading-[1.1] tracking-[-0.03em] text-slate-900 dark:text-white">{t('study.mix.h1')}</h1>
        <p className="mt-2 text-[13.5px] leading-snug text-slate-600 dark:text-slate-300">{t('study.mix.sub')}</p>
      </div>

      {/* Resume hero — the one obvious action */}
      <ResumeHero onSmart={startSmart} smartPending={isPending} />

      {/* OR: smart session / mock exam */}
      <div>
        <div className="mb-3 flex items-center gap-3" aria-hidden="true">
          <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
          <span className="text-[10.5px] font-extrabold uppercase tracking-[0.13em] text-slate-400 dark:text-slate-500">{t('study.mix.or')}</span>
          <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ActionCard
            icon="sparkles"
            gradient="linear-gradient(135deg, #7C3AED, #4F46E5)"
            title={t('study.mix.smart.title')}
            desc={t('study.mix.smart.desc')}
            onClick={startSmart}
            disabled={isPending}
          />
          <ActionCard
            icon="filecheck"
            gradient="linear-gradient(135deg, #0891B2, #2563EB)"
            title={t('study.mix.exam.title')}
            desc={activeExam ? t('study.mix.exam.resumeDesc') : t('study.mix.exam.desc')}
            onClick={() => navigate('/exam')}
          />
        </div>
      </div>

      {/* Domains */}
      <div>
        <SectionLabel>{t('study.mix.domains.label')}</SectionLabel>
        <DomainGrid onLaunchDomain={handleLaunchDomain} onCustom={() => setCustomOpen(true)} />
      </div>

      {/* Custom session modal — hosts the session composer (own header/footer). */}
      <Modal
        open={customOpen}
        onClose={() => setCustomOpen(false)}
        ariaLabel={t('study.mix.custom.title')}
        size="lg"
        cardClassName="!p-0 overflow-hidden"
      >
        <SessionLauncher onClose={() => setCustomOpen(false)} />
      </Modal>
    </div>
  );
}
